import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { apiFetch, getAuthenticatedUploadUrl } from '../../api/client';
import {
  HUD_CTA_BUTTON,
  HUD_DIALOG_CONTENT,
  HUD_DIALOG_PAPER,
  HUD_DIALOG_TITLE,
} from './hudDialogStyles';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['clean'],
  ],
};

export default function EditorCISolicitacaoModal({
  open,
  onClose,
  solicitacaoId = null,
  tituloInicial = '',
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [assunto, setAssunto] = useState('');
  const [corpoHtml, setCorpoHtml] = useState('');
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!open || !solicitacaoId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setPreviewPdfUrl(null);
      try {
        const res = await apiFetch(`/api/contratos/solicitacoes/${solicitacaoId}/ci`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Erro ao carregar CI');
        if (cancelled) return;
        setAssunto(data.assunto || tituloInicial || '');
        setCorpoHtml(data.corpoHtml || '');
        if (data.ciPdfPath) {
          const raw = String(data.ciPdfPath).replace(/^uploads\//, '');
          setPreviewPdfUrl(getAuthenticatedUploadUrl(raw));
        }
      } catch (err) {
        if (!cancelled) {
          setAssunto(tituloInicial || '');
          setSnackbar({ open: true, message: err.message || 'Erro ao carregar CI', severity: 'error' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, solicitacaoId, tituloInicial]);

  const salvar = async () => {
    if (!solicitacaoId) return;
    if (!String(assunto || '').trim()) {
      setSnackbar({ open: true, message: 'Informe o assunto da CI.', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch(`/api/contratos/solicitacoes/${solicitacaoId}/ci`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assunto, corpoHtml }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar CI');
      setSnackbar({ open: true, message: 'CI salva no sistema.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Erro ao salvar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const gerarPdf = async () => {
    if (!solicitacaoId) return;
    setGenerating(true);
    try {
      await salvar();
      const res = await apiFetch(`/api/contratos/solicitacoes/${solicitacaoId}/ci/gerar-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assunto, corpoHtml }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao gerar PDF');
      const raw = String(data.pdf_path || data.ciPdfPath || '').replace(/^uploads\//, '');
      if (raw) setPreviewPdfUrl(getAuthenticatedUploadUrl(raw));
      setSnackbar({ open: true, message: 'PDF da CI gerado.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Erro ao gerar PDF', severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: HUD_DIALOG_PAPER }}
      >
        <DialogTitle sx={HUD_DIALOG_TITLE}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={800}>
              Editar CI no sistema
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'inherit' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={HUD_DIALOG_CONTENT} dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Alert severity="info">
                Preencha a CI aqui. A moldura padrão fica em Configuração → CI. Em seguida edite o TR e
                envie para autorização/assinatura.
              </Alert>
              <TextField
                label="Assunto"
                fullWidth
                size="small"
                required
                value={assunto}
                onChange={(e) => setAssunto(e.target.value)}
              />
              <Box
                sx={{
                  '& .ql-editor': { minHeight: 220 },
                  '& .ql-container': { border: '1px solid rgba(0,0,0,0.23)' },
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Corpo da CI
                </Typography>
                <ReactQuill
                  theme="snow"
                  value={corpoHtml}
                  onChange={setCorpoHtml}
                  modules={QUILL_MODULES}
                  placeholder="Digite o conteúdo do comunicado interno..."
                />
              </Box>
              {previewPdfUrl && (
                <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, overflow: 'hidden', height: 360 }}>
                  <iframe title="Preview CI" src={previewPdfUrl} width="100%" height="100%" style={{ border: 0 }} />
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Fechar
          </Button>
          <Button
            variant="outlined"
            startIcon={generating ? <CircularProgress size={16} /> : <PdfIcon />}
            disabled={loading || generating || saving}
            onClick={gerarPdf}
            sx={{ textTransform: 'none' }}
          >
            Gerar PDF
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={loading || saving}
            onClick={salvar}
            sx={{ ...HUD_CTA_BUTTON, textTransform: 'none' }}
          >
            Salvar CI
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
